'use client';

/**
 * TargetFirstScopeBuilder — target-first scope editor for the Target-Aware
 * Scoping Wizard.
 *
 * Visual language: Ultra-compact, dashboard-style UI with extremely clean
 * alignments, refined sizing, and seamless pro-grade usability.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Trash2, Pencil, Check, X,
  Globe, Code2, Smartphone, Wifi, Network, Cloud, Lock, Sparkles, FileCode2, ShieldAlert,
  ChevronRight, ChevronDown, ArrowLeft, ArrowRight,
} from 'lucide-react';
import {
  EXPOSURE_BY_ASSESSMENT,
  type AssessmentType,
  type AssetCategory,
  type BusinessAssetV2,
  type AssessmentScopeV2,
  type TargetV2,
  type EnvironmentStage,
  type AccessLevel,
  type TargetKind,
  type TestingApproach,
  type Exposure,
} from '../../lib/questionnaires';
import { ASSESSMENT_TO_CATEGORY, detectTargetType } from '../../lib/targetDetection';
import { classifyScope, type ScopeClassification } from '../../lib/scopeClassifier';
import ConfirmationModal from '../common/ConfirmationModal';

// ─── Tailwind Color Themes for Assessment Types ─────────────────────────────
const TYPE_COLOR_SCHEME: Record<AssessmentType, {
  border: string;
  borderLeft: string;
  bg: string;
  iconBg: string;
  iconText: string;
  pillText: string;
  gradient: string;
}> = {
  WEB_APP: { border: 'border-blue-100', borderLeft: 'border-l-blue-600', bg: 'bg-blue-50/20', iconBg: 'bg-blue-100/70', iconText: 'text-blue-600', pillText: 'text-blue-700 bg-blue-50 border-blue-100', gradient: 'from-blue-600 to-indigo-600' },
  API: { border: 'border-emerald-100', borderLeft: 'border-l-emerald-600', bg: 'bg-emerald-50/20', iconBg: 'bg-emerald-100/70', iconText: 'text-emerald-600', pillText: 'text-emerald-700 bg-emerald-50 border-emerald-100', gradient: 'from-emerald-600 to-teal-600' },
  MOBILE_APP: { border: 'border-violet-100', borderLeft: 'border-l-violet-600', bg: 'bg-violet-50/20', iconBg: 'bg-violet-100/70', iconText: 'text-violet-600', pillText: 'text-violet-700 bg-violet-50 border-violet-100', gradient: 'from-violet-600 to-purple-600' },
  EXTERNAL_NETWORK: { border: 'border-amber-100', borderLeft: 'border-l-amber-600', bg: 'bg-amber-50/20', iconBg: 'bg-amber-100/70', iconText: 'text-amber-600', pillText: 'text-amber-700 bg-amber-50 border-amber-100', gradient: 'from-amber-600 to-orange-600' },
  INTERNAL_NETWORK: { border: 'border-rose-100', borderLeft: 'border-l-rose-600', bg: 'bg-rose-50/20', iconBg: 'bg-rose-100/70', iconText: 'text-rose-600', pillText: 'text-rose-700 bg-rose-50 border-rose-100', gradient: 'from-rose-600 to-red-600' },
  CLOUD_CONFIG: { border: 'border-cyan-100', borderLeft: 'border-l-cyan-600', bg: 'bg-cyan-50/20', iconBg: 'bg-cyan-100/70', iconText: 'text-cyan-600', pillText: 'text-cyan-700 bg-cyan-50 border-cyan-100', gradient: 'from-cyan-600 to-sky-600' },
  CLOUD_PENTEST: { border: 'border-pink-100', borderLeft: 'border-l-pink-600', bg: 'bg-pink-50/20', iconBg: 'bg-pink-100/70', iconText: 'text-pink-600', pillText: 'text-pink-700 bg-pink-50 border-pink-100', gradient: 'from-pink-600 to-rose-600' },
  M365_REVIEW: { border: 'border-indigo-100', borderLeft: 'border-l-indigo-600', bg: 'bg-indigo-50/20', iconBg: 'bg-indigo-100/70', iconText: 'text-indigo-600', pillText: 'text-indigo-700 bg-indigo-50 border-indigo-100', gradient: 'from-indigo-600 to-violet-600' },
  VPN_FIREWALL_REVIEW: { border: 'border-yellow-100', borderLeft: 'border-l-yellow-600', bg: 'bg-yellow-50/20', iconBg: 'bg-yellow-100/70', iconText: 'text-yellow-600', pillText: 'text-yellow-700 bg-yellow-50 border-yellow-100', gradient: 'from-yellow-600 to-amber-600' },
  AI_LLM: { border: 'border-fuchsia-100', borderLeft: 'border-l-fuchsia-600', bg: 'bg-fuchsia-50/20', iconBg: 'bg-fuchsia-100/70', iconText: 'text-fuchsia-600', pillText: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-100', gradient: 'from-fuchsia-600 to-pink-600' },
  CODE_REVIEW: { border: 'border-slate-200', borderLeft: 'border-l-slate-600', bg: 'bg-slate-50/30', iconBg: 'bg-slate-100/70', iconText: 'text-slate-600', pillText: 'text-slate-700 bg-slate-50 border-slate-200', gradient: 'from-slate-600 to-zinc-600' },
};

// ─── Public API ─────────────────────────────────────────────────────────────

export function validateTargetFirstBuilder(value: unknown, sectionTitle: string): string[] {
  const misses: string[] = [];
  if (!Array.isArray(value) || value.length === 0) {
    misses.push(`${sectionTitle} → add at least one target`);
    return misses;
  }
  let targetIdx = 0;
  for (const a of value as BusinessAssetV2[]) {
    for (const s of a.assessment_scopes) {
      for (const t of s.targets) {
        targetIdx += 1;
        const lbl = t.target_value || `Target ${targetIdx}`;
        if (!t.target_value?.trim()) {
          misses.push(`${sectionTitle} → target ${targetIdx} value required`);
        }
        if (!t.environment_stage) {
          misses.push(`${sectionTitle} → ${lbl}: pick an environment`);
        }
      }
    }
  }
  return misses;
}

// ─── Per-type defaults ──────────────────────────────────────────────────────

const TARGET_KIND_DEFAULT: Record<AssessmentType, TargetKind> = {
  WEB_APP: 'BASE_URL',
  API: 'API_BASE_URL',
  MOBILE_APP: 'APK_FILE',
  EXTERNAL_NETWORK: 'CIDR',
  INTERNAL_NETWORK: 'CIDR',
  CLOUD_CONFIG: 'AWS_ACCOUNT_ID',
  CLOUD_PENTEST: 'AWS_ACCOUNT_ID',
  M365_REVIEW: 'TENANT_ID',
  VPN_FIREWALL_REVIEW: 'DEVICE_ID',
  AI_LLM: 'ENDPOINT_URL',
  CODE_REVIEW: 'REPOSITORY_URL',
};

const TESTING_APPROACH_DEFAULT: Record<AssessmentType, TestingApproach> = {
  WEB_APP: 'GREY_BOX', API: 'GREY_BOX', MOBILE_APP: 'GREY_BOX',
  EXTERNAL_NETWORK: 'GREY_BOX', INTERNAL_NETWORK: 'GREY_BOX',
  CLOUD_PENTEST: 'GREY_BOX', AI_LLM: 'GREY_BOX',
  CODE_REVIEW: 'WHITE_BOX', CLOUD_CONFIG: 'WHITE_BOX',
  M365_REVIEW: 'WHITE_BOX', VPN_FIREWALL_REVIEW: 'WHITE_BOX',
};

const ACCESS_PILL_TO_LEVELS: Record<string, AccessLevel[]> = {
  no_credentials: ['NO_CREDENTIALS'],
  test_user: ['STANDARD_USER'],
  admin: ['ADMIN'],
  multiple_roles: ['STANDARD_USER', 'ADMIN', 'MULTIPLE_USER_ROLES'],
  api_token: ['API_TOKEN'],
  oauth_client: ['OAUTH_CLIENT'],
  mtls: ['MTLS'],
  readonly_role: ['READ_ONLY_CLOUD_ROLE'],
  source_code: ['SOURCE_CODE_ACCESS'],
  vpn_access: ['VPN_INTERNAL_NETWORK'],
  tbc: ['TBC'],
};

type FormState = Record<string, unknown>;
type WizardStep = 1 | 2 | 3 | 'advanced';

type BaseField = {
  key: string;
  label: string;
  helpText?: string;
  hidden?: (f: FormState) => boolean;
  step?: WizardStep;
  required?: boolean;
};

type FormFieldDef =
  | (BaseField & { type: 'text'; placeholder?: string })
  | (BaseField & { type: 'long_text'; placeholder?: string })
  | (BaseField & { type: 'yes_no' })
  | (BaseField & { type: 'pill_choice'; options: Array<{ value: string; label: string }> })
  | (BaseField & { type: 'multi_choice'; options: Array<{ value: string; label: string }> })
  | (BaseField & { type: 'single_choice'; options: Array<{ value: string; label: string }> })
  | (BaseField & { type: 'number'; placeholder?: string });

const ENV_OPTIONS = [
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'STAGING', label: 'Staging / UAT' },
  { value: 'ISOLATED_TEST', label: 'Isolated test' },
  { value: 'DEVELOPMENT', label: 'Development' },
  { value: 'TBC', label: 'Not sure' },
];

type TypeConfig = {
  card: { label: string; description: string; addLabel: string; icon: React.ReactNode };
  fields: FormFieldDef[];
  targetPlaceholder: string;
  targetLabel: string;
};

const TYPE_CONFIG: Record<AssessmentType, TypeConfig> = {
  WEB_APP: {
    card: { label: 'Web Application', description: 'Portal, SaaS, admin panel', addLabel: 'Add web app', icon: <Globe size={13} /> },
    targetLabel: 'Base URL',
    targetPlaceholder: 'e.g. https://portal.example.com',
    fields: [
      { key: 'environment', label: 'Environment', type: 'pill_choice', required: true, options: ENV_OPTIONS, step: 1 },
      { key: 'login_required', label: 'Is login required?', type: 'yes_no', helpText: 'Pick No for fully-public sites. Pick Yes if users sign in.', step: 2 },
      {
        key: 'access_type',
        label: 'Will CTB receive access?',
        type: 'pill_choice',
        helpText: 'Drives which auth-bypass paths CTB tests.',
        step: 2,
        options: [
          { value: 'no_credentials', label: 'No credentials' },
          { value: 'test_user', label: 'Test user account' },
          { value: 'admin', label: 'Admin account' },
          { value: 'multiple_roles', label: 'Multiple user roles' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.login_required === false,
      },
      {
        key: 'user_roles',
        label: 'How many user roles?',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: 'public_only', label: 'Public only' },
          { value: '1', label: '1 role' },
          { value: '2_3', label: '2–3 roles' },
          { value: '4_plus', label: '4+ roles' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.login_required === false,
      },
      {
        key: 'pages_count_band',
        label: 'Approximate number of pages / screens',
        type: 'pill_choice',
        step: 3,
        options: [
          { value: '1_10', label: '1–10' },
          { value: '11_30', label: '11–30' },
          { value: '31_75', label: '31–75' },
          { value: '75_plus', label: '75+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'important_flows',
        label: 'Important flows',
        type: 'multi_choice',
        helpText: 'Tick the high-value flows CTB should focus on.',
        step: 3,
        options: [
          { value: 'login', label: 'Login' },
          { value: 'payment', label: 'Payment' },
          { value: 'file_upload', label: 'File upload' },
          { value: 'admin_panel', label: 'Admin panel' },
          { value: 'general', label: 'General coverage' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.login_required === false,
      },
      { key: 'excludes', label: 'Anything CTB should NOT test?', type: 'long_text', placeholder: 'e.g. payment-vendor pages, third-party SSO IdPs.', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', placeholder: 'Tech stack, sensitive flows, known issues.', step: 'advanced' },
    ],
  },
  API: {
    card: { label: 'API Service', description: 'REST, GraphQL, backend APIs', addLabel: 'Add API', icon: <Code2 size={13} /> },
    targetLabel: 'API base URL',
    targetPlaceholder: 'e.g. https://api.example.com',
    fields: [
      { key: 'environment', label: 'Environment', type: 'pill_choice', required: true, options: ENV_OPTIONS, step: 1 },
      {
        key: 'access_type',
        label: 'Will CTB receive access?',
        type: 'pill_choice',
        helpText: 'Picks the auth mechanism CTB tests.',
        step: 2,
        options: [
          { value: 'no_credentials', label: 'No credentials' },
          { value: 'api_token', label: 'API token' },
          { value: 'oauth_client', label: 'OAuth client' },
          { value: 'mtls', label: 'mTLS' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'api_type',
        label: 'API type',
        type: 'pill_choice',
        helpText: 'GraphQL / gRPC change the test surface.',
        step: 2,
        options: [
          { value: 'rest', label: 'REST' },
          { value: 'graphql', label: 'GraphQL' },
          { value: 'grpc', label: 'gRPC' },
          { value: 'soap', label: 'SOAP / XML-RPC' },
          { value: 'mixed', label: 'Mixed' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'endpoints_count_band',
        label: 'Approximate number of endpoints',
        type: 'pill_choice',
        step: 3,
        options: [
          { value: '1_20', label: '1–20' },
          { value: '21_100', label: '21–100' },
          { value: '101_300', label: '101–300' },
          { value: '300_plus', label: '300+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'doc_type',
        label: 'Documentation available',
        type: 'pill_choice',
        helpText: 'OpenAPI / Postman dramatically improves coverage.',
        step: 3,
        options: [
          { value: 'openapi', label: 'OpenAPI / Swagger' },
          { value: 'postman', label: 'Postman collection' },
          { value: 'custom', label: 'Custom docs' },
          { value: 'none', label: 'None' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'rate_limit_allowed', label: 'Is rate-limit testing allowed?', type: 'yes_no', step: 'advanced' },
      { key: 'destructive_excluded', label: 'Exclude destructive operations (DELETE, mass-mutation)?', type: 'yes_no', helpText: 'Default: yes — destructive ops excluded unless explicitly authorised.', step: 'advanced' },
      { key: 'excludes', label: 'Anything CTB should NOT test?', type: 'long_text', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  MOBILE_APP: {
    card: { label: 'Mobile Application', description: 'iOS, Android, APK, IPA', addLabel: 'Add mobile app', icon: <Smartphone size={13} /> },
    targetLabel: 'App build, App Store URL, or bundle ID',
    targetPlaceholder: 'e.g. AcmeBank-v2.4.1.apk · com.acme.bankapp',
    fields: [
      {
        key: 'platforms',
        label: 'Platforms',
        type: 'multi_choice',
        helpText: 'iOS and Android each require a different toolchain.',
        step: 1,
        options: [
          { value: 'ios', label: 'iOS' },
          { value: 'android', label: 'Android' },
        ],
      },
      { key: 'environment', label: 'Environment', type: 'pill_choice', required: true, options: ENV_OPTIONS, step: 1 },
      { key: 'build_available', label: 'Will CTB receive the APK / IPA?', type: 'yes_no', helpText: 'Static + dynamic analysis depth depends on having the binary.', step: 2 },
      { key: 'test_credentials_provided', label: 'Will test credentials be provided?', type: 'yes_no', step: 2 },
      {
        key: 'app_flows_count',
        label: 'Approximate number of major app flows',
        type: 'pill_choice',
        helpText: 'A "flow" is a complete user journey — onboarding, payment, profile.',
        step: 3,
        options: [
          { value: '1_5', label: '1–5' },
          { value: '6_15', label: '6–15' },
          { value: '16_plus', label: '16+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'backend_in_scope', label: 'Is the mobile-backend API included in the same engagement?', type: 'yes_no', step: 3 },
      { key: 'sensitive_local_storage', label: 'Does the app store sensitive data locally?', type: 'yes_no', step: 'advanced' },
      { key: 'biometrics_in_scope', label: 'Are biometric auth flows (FaceID/TouchID) in scope?', type: 'yes_no', step: 'advanced' },
      { key: 'reverse_engineering_allowed', label: 'Is reverse-engineering of the binary allowed?', type: 'yes_no', helpText: 'Default: yes. Most mobile-pentest depth requires this.', hidden: (f) => f.build_available === false, step: 'advanced' },
      { key: 'rooted_devices_allowed', label: 'Can CTB use rooted / jailbroken test devices?', type: 'yes_no', hidden: (f) => f.build_available === false, step: 'advanced' },
      { key: 'excludes', label: 'Anything CTB should NOT test?', type: 'long_text', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  EXTERNAL_NETWORK: {
    card: { label: 'External Network', description: 'Public IPs, CIDRs, hostnames', addLabel: 'Add external network', icon: <Wifi size={13} /> },
    targetLabel: 'IPs / CIDR ranges / hostnames',
    targetPlaceholder: 'e.g. 203.0.113.0/24, mail.example.com',
    fields: [
      { key: 'environment', label: 'Environment', type: 'pill_choice', required: true, options: ENV_OPTIONS, step: 1 },
      {
        key: 'live_hosts_count_band',
        label: 'Approximate live (responding) hosts',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '1_5', label: '1–5' },
          { value: '6_25', label: '6–25' },
          { value: '26_100', label: '26–100' },
          { value: '100_plus', label: '100+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'services_in_scope',
        label: 'Services in scope',
        type: 'multi_choice',
        helpText: 'Tick every protocol CTB should test.',
        step: 3,
        options: [
          { value: 'http', label: 'HTTP / HTTPS' },
          { value: 'ssh', label: 'SSH' },
          { value: 'smtp', label: 'SMTP / mail' },
          { value: 'dns', label: 'DNS' },
          { value: 'vpn', label: 'VPN gateways' },
          { value: 'rdp', label: 'RDP' },
          { value: 'database', label: 'Exposed databases' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'excludes', label: 'Out-of-scope IPs / services (do NOT touch)', type: 'long_text', placeholder: 'One per line.', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  INTERNAL_NETWORK: {
    card: { label: 'Internal Network', description: 'Post-perimeter / insider-view testing', addLabel: 'Add internal network', icon: <Network size={13} /> },
    targetLabel: 'Internal IPs subnets AD domain',
    targetPlaceholder: 'e.g. 10.10.0.0/16, corp.example.local',
    fields: [
      { key: 'environment', label: 'Environment', type: 'pill_choice', required: true, options: ENV_OPTIONS, step: 1 },
      {
        key: 'access_method',
        label: 'How will CTB get on the internal network?',
        type: 'single_choice',
        helpText: 'VPN is fastest. Shipped laptop is most realistic.',
        step: 2,
        options: [
          { value: 'vpn', label: 'VPN credentials' },
          { value: 'shipped_laptop', label: 'Hardened laptop shipped' },
          { value: 'onsite_visit', label: 'On-site visit' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'hosts_count_band',
        label: 'Approximate number of internal hosts',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '1_50', label: '1–50' },
          { value: '51_250', label: '51–250' },
          { value: '251_1000', label: '251–1000' },
          { value: '1000_plus', label: '1000+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'ad_in_scope', label: 'Active Directory in scope?', type: 'yes_no', helpText: 'AD is the biggest internal-network target.', step: 3 },
      {
        key: 'testing_depth',
        label: 'Testing depth',
        type: 'multi_choice',
        helpText: 'Tick every depth CTB should reach.',
        step: 3,
        options: [
          { value: 'basic_scan', label: 'Basic internal scan' },
          { value: 'credentialed', label: 'Credentialed internal testing' },
          { value: 'privilege_escalation', label: 'Privilege escalation' },
          { value: 'lateral_movement', label: 'Lateral movement' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'password_spraying_allowed', label: 'Is password spraying allowed?', type: 'yes_no', hidden: (f) => f.ad_in_scope === false, step: 'advanced' },
      { key: 'excludes', label: 'Anything CTB should NOT test?', type: 'long_text', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  CLOUD_CONFIG: {
    card: { label: 'Cloud Review', description: 'AWS / Azure / GCP config review', addLabel: 'Add cloud account', icon: <Cloud size={13} /> },
    targetLabel: 'Cloud account / subscription / project ID',
    targetPlaceholder: 'e.g. AWS account 123456789012',
    fields: [
      {
        key: 'provider',
        label: 'Cloud provider',
        type: 'pill_choice',
        required: true,
        step: 1,
        options: [
          { value: 'aws', label: 'AWS' },
          { value: 'azure', label: 'Azure' },
          { value: 'gcp', label: 'GCP' },
          { value: 'multi', label: 'Multi-cloud' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'accounts_count_band',
        label: 'Number of accounts / projects',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '1', label: '1' },
          { value: '2_5', label: '2–5' },
          { value: '6_20', label: '6–20' },
          { value: '20_plus', label: '20+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'access_mode',
        label: 'How will CTB access?',
        type: 'single_choice',
        helpText: 'Read-only IAM role is the recommended pattern.',
        step: 2,
        options: [
          { value: 'readonly_role', label: 'Read-only IAM role' },
          { value: 'test_account', label: 'Test account inside tenant' },
          { value: 'sso', label: 'Federated SSO' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'review_scope',
        label: 'Review scope',
        type: 'single_choice',
        step: 3,
        options: [
          { value: 'config_only', label: 'Configuration only' },
          { value: 'config_plus_iam', label: 'Configuration + IAM' },
          { value: 'full_posture', label: 'Full posture review' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'kubernetes_in_scope', label: 'Is Kubernetes (EKS/AKS/GKE) in scope?', type: 'yes_no', step: 3 },
      { key: 'storage_buckets_in_scope', label: 'Are storage buckets / blob storage in scope?', type: 'yes_no', step: 3 },
      { key: 'review_focus', label: 'Anything specific CTB should focus on?', type: 'long_text', placeholder: 'IAM mis-configs, public buckets, secrets handling.', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  CLOUD_PENTEST: {
    card: { label: 'Cloud Pentest', description: 'Active cloud exploitation, IAM privesc', addLabel: 'Add cloud pentest', icon: <ShieldAlert size={13} /> },
    targetLabel: 'Cloud account / subscription / project ID',
    targetPlaceholder: 'e.g. AWS account 123456789012',
    fields: [
      {
        key: 'provider',
        label: 'Cloud provider',
        type: 'pill_choice',
        required: true,
        step: 1,
        options: [
          { value: 'aws', label: 'AWS' },
          { value: 'azure', label: 'Azure' },
          { value: 'gcp', label: 'GCP' },
          { value: 'multi', label: 'Multi-cloud' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'accounts_count_band',
        label: 'Number of accounts / projects',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '1', label: '1' },
          { value: '2_5', label: '2–5' },
          { value: '6_20', label: '6–20' },
          { value: '20_plus', label: '20+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'access_mode',
        label: 'How will CTB access?',
        type: 'single_choice',
        step: 2,
        options: [
          { value: 'test_account', label: 'Test account inside tenant' },
          { value: 'readonly_role', label: 'Read-only IAM role' },
          { value: 'sso', label: 'Federated SSO' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'iam_privesc_in_scope', label: 'Are IAM privilege-escalation paths in scope?', type: 'yes_no', helpText: 'Without this, the engagement is essentially read-only.', step: 3 },
      { key: 'kubernetes_in_scope', label: 'Is Kubernetes in scope?', type: 'yes_no', step: 3 },
      { key: 'provider_approval_needed', label: 'Is cloud-provider testing approval needed?', type: 'yes_no', helpText: 'AWS/Azure/GCP usually require pre-approval.', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  M365_REVIEW: {
    card: { label: 'M365 / SharePoint', description: 'Permissions + exposure review', addLabel: 'Add M365 tenant', icon: <Cloud size={13} /> },
    targetLabel: 'M365 tenant',
    targetPlaceholder: 'e.g. acme.onmicrosoft.com',
    fields: [
      {
        key: 'tenants_count',
        label: 'Number of tenants',
        type: 'pill_choice',
        step: 1,
        options: [
          { value: '1', label: '1' },
          { value: '2_3', label: '2–3' },
          { value: '4_plus', label: '4+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'user_count_band',
        label: 'Approximate licensed-user count',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: 'lte_100', label: '≤ 100' },
          { value: '101_500', label: '101–500' },
          { value: '500_plus', label: '500+' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'scope_breadth',
        label: 'Review scope',
        type: 'single_choice',
        step: 2,
        options: [
          { value: 'sharepoint_only', label: 'SharePoint only' },
          { value: 'sharepoint_onedrive', label: 'SharePoint + OneDrive' },
          { value: 'teams_sharepoint', label: 'Teams + SharePoint' },
          { value: 'full_m365', label: 'Full M365' },
          { value: 'full_plus_entra', label: 'M365 + Entra ID' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'external_sharing_enabled', label: 'Is external sharing enabled?', type: 'yes_no', step: 3 },
      { key: 'sharepoint_sites', label: 'SharePoint site-collection count', type: 'number', placeholder: 'e.g. 12', step: 3 },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  VPN_FIREWALL_REVIEW: {
    card: { label: 'VPN / Firewall', description: 'Remote-access + ruleset review', addLabel: 'Add VPN / firewall', icon: <Lock size={13} /> },
    targetLabel: 'Device ID or config-export reference',
    targetPlaceholder: 'e.g. fw-edge-01 · config-export.json',
    fields: [
      {
        key: 'device_type',
        label: 'What type of device?',
        type: 'pill_choice',
        step: 1,
        options: [
          { value: 'vpn', label: 'VPN gateway' },
          { value: 'firewall', label: 'Firewall' },
          { value: 'both', label: 'Both' },
        ],
      },
      {
        key: 'vpn_gateways_count',
        label: 'Number of VPN gateways',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '0', label: '0' },
          { value: '1', label: '1' },
          { value: '2_5', label: '2–5' },
          { value: '6_plus', label: '6+' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.device_type === 'firewall',
      },
      {
        key: 'firewall_devices_count',
        label: 'Number of firewall devices / clusters',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: '1', label: '1' },
          { value: '2_5', label: '2–5' },
          { value: '6_plus', label: '6+' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.device_type === 'vpn',
      },
      {
        key: 'ruleset_size',
        label: 'Ruleset size (largest device)',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: 'lte_100', label: '≤ 100 rules' },
          { value: '101_500', label: '101–500 rules' },
          { value: '500_plus', label: '500+ rules' },
          { value: 'tbc', label: 'Not sure' },
        ],
        hidden: (f) => f.device_type === 'vpn',
      },
      {
        key: 'auth_methods',
        label: 'VPN auth methods',
        type: 'multi_choice',
        helpText: 'Tick every auth method actually in use.',
        step: 3,
        options: [
          { value: 'username_password', label: 'Username + password' },
          { value: 'certificate', label: 'Client certificate' },
          { value: 'mfa', label: 'MFA enforced' },
          { value: 'sso', label: 'SSO / SAML / OIDC' },
        ],
        hidden: (f) => f.device_type === 'firewall',
      },
      { key: 'active_vpn_testing_allowed', label: 'Is active VPN testing allowed?', type: 'yes_no', hidden: (f) => f.device_type === 'firewall', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  AI_LLM: {
    card: { label: 'AI / LLM Application', description: 'Chatbots, prompt workflows', addLabel: 'Add AI feature', icon: <Sparkles size={13} /> },
    targetLabel: 'Endpoint URL or model identifier',
    targetPlaceholder: 'e.g. https://chat.example.com or claude-3-5-sonnet',
    fields: [
      {
        key: 'model_provider',
        label: 'Model provider',
        type: 'pill_choice',
        step: 1,
        options: [
          { value: 'openai', label: 'OpenAI' },
          { value: 'anthropic', label: 'Anthropic' },
          { value: 'google', label: 'Google' },
          { value: 'self_hosted', label: 'Self-hosted' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'use_case_band',
        label: 'AI use case',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: 'chatbot', label: 'Customer chatbot' },
          { value: 'internal_assistant', label: 'Internal assistant' },
          { value: 'rag', label: 'RAG / knowledge search' },
          { value: 'agent', label: 'Agent with tools' },
          { value: 'api_workflow', label: 'AI API / workflow' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'data_sources',
        label: 'Data the AI accesses',
        type: 'single_choice',
        step: 2,
        options: [
          { value: 'no_external', label: 'No external data' },
          { value: 'public_docs', label: 'Public docs' },
          { value: 'internal_docs', label: 'Internal docs' },
          { value: 'sensitive_data', label: 'Sensitive data' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'actions_capability',
        label: 'Can the AI take actions?',
        type: 'single_choice',
        helpText: 'Write actions raise impact of injection.',
        step: 3,
        options: [
          { value: 'no_actions', label: 'No' },
          { value: 'reads_only', label: 'Reads data only' },
          { value: 'calls_tools', label: 'Calls tools / APIs' },
          { value: 'updates_data', label: 'Can create / update / delete' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'customer_facing', label: 'Is the AI customer-facing?', type: 'yes_no', step: 3 },
      { key: 'use_case', label: 'What does the AI do?', type: 'long_text', placeholder: 'e.g. Customer chatbot answering product questions.', step: 3 },
      { key: 'tools_called', label: 'Does the AI call tools / external APIs?', type: 'yes_no', step: 'advanced' },
      { key: 'rag_in_scope', label: 'Is RAG pipeline in scope?', type: 'yes_no', hidden: (f) => f.tools_called === false, step: 'advanced' },
      { key: 'jailbreak_in_scope', label: 'Is prompt-injection testing in scope?', type: 'yes_no', step: 'advanced' },
      { key: 'worst_case_scenario', label: 'Worst thing the AI could do if abused?', type: 'long_text', placeholder: 'e.g. leak the system prompt.', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
  CODE_REVIEW: {
    card: { label: 'Secure Code Review', description: 'GitHub / GitLab source review', addLabel: 'Add code review', icon: <FileCode2 size={13} /> },
    targetLabel: 'Repository URL',
    targetPlaceholder: 'e.g. github.com/acme/customer-portal',
    fields: [
      { key: 'branch', label: 'Branch to review', type: 'text', placeholder: 'e.g. main / master', helpText: 'CTB freezes against SHA at start.', step: 1 },
      {
        key: 'code_size_band',
        label: 'Approximate code size',
        type: 'pill_choice',
        step: 2,
        options: [
          { value: 'lte_50k', label: '≤ 50K LOC' },
          { value: '50k_200k', label: '50K–200K LOC' },
          { value: '200k_plus', label: '200K+ LOC' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      {
        key: 'languages',
        label: 'Languages used',
        type: 'multi_choice',
        step: 2,
        options: [
          { value: 'js_ts', label: 'JS / TS' },
          { value: 'python', label: 'Python' },
          { value: 'go', label: 'Go' },
          { value: 'java', label: 'Java / Kotlin' },
          { value: 'csharp', label: 'C# / .NET' },
          { value: 'php', label: 'PHP' },
          { value: 'ruby', label: 'Ruby' },
          { value: 'rust', label: 'Rust' },
          { value: 'cpp', label: 'C / C++' },
        ],
      },
      {
        key: 'review_boundary',
        label: 'Review boundary',
        type: 'single_choice',
        step: 3,
        options: [
          { value: 'whole_repo', label: 'Whole repository' },
          { value: 'specific_folder', label: 'Specific folder / service' },
          { value: 'specific_commits', label: 'Specific commit range' },
          { value: 'tbc', label: 'Not sure' },
        ],
      },
      { key: 'focus_areas', label: 'Specific high-risk areas to focus on', type: 'long_text', placeholder: 'e.g. auth + session, payment.', step: 3 },
      { key: 'no_build_docs', label: 'Are build docs missing or incomplete?', type: 'yes_no', step: 'advanced' },
      { key: 'notes', label: 'Anything else CTB should know?', type: 'long_text', step: 'advanced' },
    ],
  },
};

const ENV_LABEL: Record<EnvironmentStage, string> = {
  PRODUCTION: 'Production', STAGING: 'Staging',
  ISOLATED_TEST: 'Isolated test', DEVELOPMENT: 'Development', TBC: 'Env TBC',
};

const newId = (prefix: string) =>
  `${prefix}_${
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36)
  }`;

function buildTargetAndDetails(
  assessmentType: AssessmentType,
  form: FormState,
): { target: TargetV2; details: Record<string, unknown> | undefined } {
  const allowedExposure = EXPOSURE_BY_ASSESSMENT[assessmentType];
  const exposure: Exposure | null = allowedExposure.length === 1 ? allowedExposure[0] : null;

  const accessPill = typeof form.access_type === 'string' ? (form.access_type as string) : '';
  const access_levels = ACCESS_PILL_TO_LEVELS[accessPill] ?? [];

  const target: TargetV2 = {
    target_id: newId('target'),
    target_value: typeof form.target_value === 'string' ? (form.target_value as string).trim() : null,
    target_kind: TARGET_KIND_DEFAULT[assessmentType],
    exposure,
    environment_stage: (form.environment as EnvironmentStage | null) ?? null,
    testing_approach: TESTING_APPROACH_DEFAULT[assessmentType],
    access_levels,
  };

  const details: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(form)) {
    if (k === 'target_value' || k === 'environment' || k === 'access_type') continue;
    if (v === undefined || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    details[k] = v;
  }

  const classification = classifyScope(assessmentType, form);
  if (classification) details.__classification = classification;

  return { target, details: Object.keys(details).length > 0 ? details : undefined };
}

type Props = {
  value: unknown;
  onChange: (next: BusinessAssetV2[]) => void;
  readOnly: boolean;
};

export default function TargetFirstScopeBuilder({ value, onChange, readOnly }: Props) {
  const assets = useMemo<BusinessAssetV2[]>(
    () => (Array.isArray(value) ? (value as BusinessAssetV2[]) : []),
    [value],
  );

  type ModalState =
    | { kind: 'closed' }
    | { kind: 'adding'; type: AssessmentType }
    | { kind: 'editing'; type: AssessmentType; assetId: string; scopeId: string; targetId: string };

  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [targetIdToDelete, setTargetIdToDelete] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) =>
    setExpandedRows((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const commitTarget = (type: AssessmentType, form: FormState): BusinessAssetV2[] => {
    const { target, details } = buildTargetAndDetails(type, form);
    const targetValueStr = target.target_value ?? '';
    const detection = detectTargetType(targetValueStr);

    const groupingName = detection?.suggestedAssetName ?? targetValueStr;
    const groupingAsset = assets.find((a) => a.asset_name === groupingName);

    const newScope: AssessmentScopeV2 = {
      scope_id: newId('scope'),
      assessment_type: type,
      targets: [target],
      details,
    };

    if (groupingAsset) {
      return assets.map((a) =>
        a.asset_id !== groupingAsset.asset_id
          ? a
          : { ...a, assessment_scopes: [...a.assessment_scopes, newScope] },
      );
    }
    const category: AssetCategory = ASSESSMENT_TO_CATEGORY[type];
    const newAsset: BusinessAssetV2 = {
      asset_id: newId('asset'),
      asset_name: groupingName,
      asset_category: category,
      criticality: null,
      owner: { name: null, role: null },
      assessment_scopes: [newScope],
    };
    return [...assets, newAsset];
  };

  const updateExistingTarget = (
    assetId: string,
    scopeId: string,
    targetId: string,
    type: AssessmentType,
    form: FormState,
  ): BusinessAssetV2[] => {
    const { target, details } = buildTargetAndDetails(type, form);
    return assets.map((a) =>
      a.asset_id !== assetId
        ? a
        : {
            ...a,
            assessment_scopes: a.assessment_scopes.map((s) =>
              s.scope_id !== scopeId
                ? s
                : {
                    ...s,
                    assessment_type: type,
                    details,
                    targets: s.targets.map((t) =>
                      t.target_id !== targetId
                        ? t
                        : { ...target, target_id: t.target_id },
                    ),
                  },
            ),
          },
    );
  };

  const removeTarget = (targetId: string) => {
    setTargetIdToDelete(targetId);
  };

  const performRemoveTarget = (targetId: string) => {
    const next = assets
      .map((a) => ({
        ...a,
        assessment_scopes: a.assessment_scopes
          .map((s) => ({ ...s, targets: s.targets.filter((t) => t.target_id !== targetId) }))
          .filter((s) => s.targets.length > 0),
      }))
      .filter((a) => a.assessment_scopes.length > 0);
    onChange(next);
  };

  const openAddModal = (type: AssessmentType) => setModal({ kind: 'adding', type });
  const openEditModal = (assetId: string, scopeId: string, target: TargetV2, type: AssessmentType) =>
    setModal({ kind: 'editing', type, assetId, scopeId, targetId: target.target_id });

  // Group saved targets by assessment_type for the summary view.
  const grouped = useMemo(() => {
    const map = new Map<
      AssessmentType,
      Array<{ asset: BusinessAssetV2; scope: AssessmentScopeV2; target: TargetV2 }>
    >();
    for (const a of assets) {
      for (const s of a.assessment_scopes) {
        if (!s.assessment_type) continue;
        const list = map.get(s.assessment_type) ?? [];
        for (const t of s.targets) list.push({ asset: a, scope: s, target: t });
        map.set(s.assessment_type, list);
      }
    }
    return map;
  }, [assets]);

  return (
    <div className="flex flex-col gap-6 w-full text-slate-800">
      {/* Unified Compact Targets Board */}
      {assets.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-slate-900">Configured targets</h3>
              <span className="bg-slate-100 text-slate-650 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-slate-200/40">
                {assets.reduce((sum, a) => sum + a.assessment_scopes.reduce((sSum, s) => sSum + s.targets.length, 0), 0)} target
              </span>
            </div>
            {!readOnly && (
              <button
                type="button"
                className="text-xs font-bold text-slate-700 hover:text-slate-900 flex items-center gap-1 cursor-pointer bg-transparent border-0"
                onClick={() => setModal({ kind: 'adding', type: 'WEB_APP' })}
              >
                <Plus size={14} /> Add target
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            {Array.from(grouped.entries()).map(([type, rows]) =>
              rows.map((row) => (
                <SummaryRow
                  key={row.target.target_id}
                  type={type}
                  target={row.target}
                  details={row.scope.details}
                  expanded={expandedRows.has(row.target.target_id)}
                  readOnly={readOnly}
                  onToggle={() => toggleRow(row.target.target_id)}
                  onEdit={() => openEditModal(row.asset.asset_id, row.scope.scope_id, row.target, type)}
                  onRemove={() => removeTarget(row.target.target_id)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Sleek, Luxurious Category Picker Board */}
      {!readOnly && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col gap-1 select-none">
            <h3 className="text-sm font-bold text-slate-900">Add another target</h3>
            <p className="text-xs text-slate-400 font-medium">
              Pick a category. The system maps it to the right CTB methodology automatically.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(Object.keys(TYPE_CONFIG) as AssessmentType[]).map((type) => (
              <button
                key={type}
                type="button"
                className="group bg-white border border-slate-100/80 hover:border-[#58CC02] rounded-xl p-4 flex flex-col items-start gap-4 transition-all duration-200 hover:shadow-md cursor-pointer text-left select-none"
                onClick={() => openAddModal(type)}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="w-9 h-9 rounded-lg bg-[#e8f5e9] flex items-center justify-center text-[#081549] shrink-0 shadow-sm text-xs font-bold">
                    {TYPE_CONFIG[type].card.icon}
                  </div>
                  <ChevronRight size={14} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition duration-150" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-xs font-bold text-slate-900 tracking-wide">
                    {TYPE_CONFIG[type].card.label}
                  </span>
                  <span className="text-[11px] text-slate-400 font-medium leading-normal whitespace-normal">
                    {TYPE_CONFIG[type].card.description}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      {modal.kind !== 'closed' && (
        <TargetModal
          type={modal.type}
          existing={modal.kind === 'editing' ? findTarget(assets, modal.targetId) : null}
          existingScopeDetails={modal.kind === 'editing' ? findScopeDetails(assets, modal.scopeId) : undefined}
          readOnly={readOnly}
          onClose={() => setModal({ kind: 'closed' })}
          onSave={(form) => {
            if (modal.kind === 'adding') {
              onChange(commitTarget(modal.type, form));
            } else {
              onChange(updateExistingTarget(modal.assetId, modal.scopeId, modal.targetId, modal.type, form));
            }
            setModal({ kind: 'closed' });
          }}
          onAddAnother={
            modal.kind === 'adding'
              ? (form) => {
                  onChange(commitTarget(modal.type, form));
                }
              : undefined
          }
        />
      )}
      {targetIdToDelete && (
        <ConfirmationModal
          isOpen={targetIdToDelete !== null}
          onClose={() => setTargetIdToDelete(null)}
          onConfirm={() => {
            performRemoveTarget(targetIdToDelete);
            setTargetIdToDelete(null);
          }}
          title="Remove Target"
          description="Are you sure you want to remove this target from your security scope?"
          warning="Removing this target will delete any custom configurations or scope answers associated with it."
          confirmText="Remove Target"
          cancelText="Cancel"
        />
      )}
    </div>
  );
}

function findTarget(assets: BusinessAssetV2[], id: string): TargetV2 | null {
  for (const a of assets) {
    for (const s of a.assessment_scopes) {
      for (const t of s.targets) {
        if (t.target_id === id) return t;
      }
    }
  }
  return null;
}

function findScopeDetails(assets: BusinessAssetV2[], id: string): Record<string, unknown> | undefined {
  for (const a of assets) {
    for (const s of a.assessment_scopes) {
      if (s.scope_id === id) return s.details;
    }
  }
  return undefined;
}

// ─── Summary row — collapsed by default ─────────────────────────────────────

function SummaryRow({
  type, target, details, expanded, readOnly, onToggle, onEdit, onRemove,
}: {
  type: AssessmentType;
  target: TargetV2;
  details: Record<string, unknown> | undefined;
  expanded: boolean;
  readOnly: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const envLabel = target.environment_stage ? ENV_LABEL[target.environment_stage] : '';
  const accessLabel = accessLabelOf(target.access_levels);
  const classification = details?.__classification as ScopeClassification | undefined;
  const config = TYPE_CONFIG[type];

  return (
    <div className="flex flex-col border border-slate-100 rounded-xl bg-white shadow-sm overflow-hidden select-none hover:border-slate-200 transition-all duration-200">
      <div className="p-4 sm:p-5 flex items-center gap-5 flex-wrap sm:flex-nowrap">

        {/* Brand Icon Box (dark navy blue) */}
        <div className="w-10 h-10 rounded-lg bg-[#081549] text-white flex items-center justify-center shrink-0 shadow-sm shadow-[#081549]/20">
          <div className="text-sm text-white">
            {config.card.icon}
          </div>
        </div>

        {/* Title and Method Description */}
        <div className="w-44 shrink-0 pr-2 text-left">
          <h4 className="text-sm font-bold text-slate-800 leading-tight">
            {config.card.label}
          </h4>
          <span className="text-[11px] text-slate-400 font-medium block mt-0.5 leading-normal">
            {config.targetLabel}
          </span>
        </div>

        {/* Target Value Box */}
        <div className="flex-1 min-w-[180px] max-w-md text-left">
          <div className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200/80 rounded-xl text-xs text-slate-650 font-mono font-medium truncate select-all">
            {target.target_value || '(no value)'}
          </div>
        </div>

        {/* Environment Stage Badge */}
        <div className="shrink-0">
          {target.environment_stage && (
            <span className={`px-3 py-1 text-xs font-bold rounded-xl border ${
              target.environment_stage === 'PRODUCTION'
                ? 'bg-red-50 text-red-700 border-red-100'
                : target.environment_stage === 'STAGING'
                  ? 'bg-amber-50 text-amber-700 border-amber-100'
                  : 'bg-[#e8f5e9] text-emerald-700 font-bold border-transparent'
            }`}>
              {ENV_LABEL[target.environment_stage]}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {!readOnly && (
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="p-1.5 border border-slate-200 text-blue-600 hover:bg-slate-50 hover:border-slate-350 rounded-lg transition duration-150 cursor-pointer flex items-center justify-center bg-white"
              onClick={onEdit}
              title="Edit target"
            >
              <Pencil size={13} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              className="p-1.5 border border-slate-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-lg transition duration-150 cursor-pointer flex items-center justify-center bg-white"
              onClick={onRemove}
              title="Remove target"
            >
              <Trash2 size={13} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-3 pt-1 bg-slate-50/40 border-t border-slate-100 flex flex-col gap-2">
          <SummaryDetails type={type} details={details} />
        </div>
      )}
    </div>
  );
}

function accessLabelOf(levels: AccessLevel[]): string {
  if (levels.includes('NO_CREDENTIALS')) return 'No credentials';
  if (levels.includes('ADMIN')) return 'Admin';
  if (levels.includes('MULTIPLE_USER_ROLES')) return 'Multiple roles';
  if (levels.includes('STANDARD_USER')) return 'Test user';
  if (levels.includes('API_TOKEN')) return 'API token';
  if (levels.includes('READ_ONLY_CLOUD_ROLE')) return 'Read-only role';
  if (levels.includes('TBC')) return 'Access TBC';
  return '';
}

/** Compact Detail list */
function SummaryDetails({
  type, details,
}: {
  type: AssessmentType;
  details: Record<string, unknown> | undefined;
}) {
  if (!details) return null;
  const config = TYPE_CONFIG[type];
  const rows: Array<{ label: string; value: string }> = [];
  for (const f of config.fields) {
    const v = details[f.key];
    if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    rows.push({ label: f.label, value: displayValue(f, v) });
  }
  if (rows.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-white border border-slate-200/50 rounded-xl p-4 shadow-sm mt-1">
      {rows.map((r) => (
        <div key={r.label} className="flex flex-col gap-0.5">
          <dt className="text-slate-400 font-extrabold uppercase text-[8px] tracking-wider leading-none select-none">{r.label}</dt>
          <dd className="text-slate-800 font-bold text-xs select-all break-words leading-relaxed">{r.value}</dd>
        </div>
      ))}
    </div>
  );
}

function displayValue(field: FormFieldDef, raw: unknown): string {
  if (field.type === 'yes_no') return raw === true ? 'Yes' : raw === false ? 'No' : '';
  if (field.type === 'multi_choice') {
    const arr = Array.isArray(raw) ? (raw as string[]) : [];
    return arr.map((v) => field.options.find((o) => o.value === v)?.label ?? v).join(', ');
  }
  if (field.type === 'pill_choice' || field.type === 'single_choice') {
    const v = typeof raw === 'string' ? raw : '';
    return field.options.find((o) => o.value === v)?.label ?? v;
  }
  if (field.type === 'number') return typeof raw === 'number' ? String(raw) : '';
  return typeof raw === 'string' ? raw : '';
}

const validateTargetValue = (type: AssessmentType, val: string): { isValid: boolean; error?: string } => {
  const cleanVal = val.trim();
  if (cleanVal.length === 0) {
    return { isValid: false, error: 'Target value is required' };
  }

  // Very loose regex patterns to allow flexible entries (e.g. domains without protocols for web apps)
  const DOMAIN_OR_URL_REG = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$|^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
  const IP_REG = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
  const IP_CIDR_REG = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}\/[0-9]{1,2}$/;

  switch (type) {
    case 'WEB_APP':
      if (!DOMAIN_OR_URL_REG.test(cleanVal)) {
        return { isValid: false, error: 'Please enter a valid domain (e.g. example.com) or URL (e.g. https://example.com)' };
      }
      break;
    case 'API':
      if (!DOMAIN_OR_URL_REG.test(cleanVal) && !cleanVal.includes('/') && cleanVal.length < 5) {
        return { isValid: false, error: 'Please enter a valid API base URL, endpoint, or Swagger/JSON schema link' };
      }
      break;
    case 'MOBILE_APP':
      const BUNDLE_ID_REG = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
      if (!BUNDLE_ID_REG.test(cleanVal) && !DOMAIN_OR_URL_REG.test(cleanVal) && cleanVal.length < 3) {
        return { isValid: false, error: 'Enter a valid Bundle/Package ID (e.g. com.company.app), App Store URL, or App Name' };
      }
      break;
    case 'EXTERNAL_NETWORK':
    case 'INTERNAL_NETWORK':
    case 'VPN_FIREWALL_REVIEW':
      const items = cleanVal.split(/[\s,]+/);
      for (const item of items) {
        if (item) {
          const isIP = IP_REG.test(item);
          const isCIDR = IP_CIDR_REG.test(item);
          const isDomain = DOMAIN_OR_URL_REG.test(item);
          if (!isIP && !isCIDR && !isDomain) {
            return { isValid: false, error: `Invalid IP, CIDR range, or hostname: "${item}"` };
          }
        }
      }
      break;
    case 'CODE_REVIEW':
      const GIT_REG = /^(git@|https?:\/\/|ftps?:\/\/)?([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+[:\/])?([a-zA-Z0-9._\/-]+)\/?$/;
      if (!GIT_REG.test(cleanVal) && cleanVal.length < 3) {
        return { isValid: false, error: 'Please enter a valid repository URL, Git SSH address, or branch path' };
      }
      break;
    case 'CLOUD_CONFIG':
      const AWS_REG = /^\d{12}$/;
      const GUID_REG = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      if (!AWS_REG.test(cleanVal) && !GUID_REG.test(cleanVal) && !DOMAIN_OR_URL_REG.test(cleanVal) && cleanVal.length < 4) {
        return { isValid: false, error: 'Enter a valid Cloud Account ID, GCP Project ID, Tenant ID, or Console URL' };
      }
      break;
    default:
      if (cleanVal.length < 2) {
        return { isValid: false, error: 'Please enter a valid target value' };
      }
      break;
  }
  return { isValid: true };
};

// ─── Modal — step-by-step wizard ────────────────────────────────────────────

function TargetModal({
  type,
  existing,
  existingScopeDetails,
  readOnly,
  onClose,
  onSave,
  onAddAnother,
}: {
  type: AssessmentType;
  existing: TargetV2 | null;
  existingScopeDetails: Record<string, unknown> | undefined;
  readOnly: boolean;
  onClose: () => void;
  onSave: (form: FormState) => void;
  onAddAnother?: (form: FormState) => void;
}) {
  const config = TYPE_CONFIG[type];

  const seedForm = useMemo<FormState>(() => {
    if (!existing) return {};
    const accessPill = pillFromAccessLevels(existing.access_levels);
    const { __classification: _ignored, ...detailsSeed } = (existingScopeDetails ?? {}) as Record<string, unknown>;
    void _ignored;
    return {
      target_value: existing.target_value ?? '',
      environment: existing.environment_stage ?? null,
      access_type: accessPill,
      ...detailsSeed,
    };
  }, [existing, existingScopeDetails]);

  const [form, setForm] = useState<FormState>(seedForm);
  const [resetCounter, setResetCounter] = useState(0);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const targetValueStr = typeof form.target_value === 'string' ? (form.target_value as string).trim() : '';

  useEffect(() => {
    if (resetCounter === 0) return;
    setForm({});
    setStep(1);
setAdvancedOpen(false);
  }, [resetCounter]);

  const setField = (key: string, value: unknown) => setForm((cur) => ({ ...cur, [key]: value }));

  const targetValidation = useMemo(() => {
    return validateTargetValue(type, targetValueStr);
  }, [type, targetValueStr]);

  const targetValueValid = targetValidation.isValid;

  const fieldsAtStep = (s: WizardStep): FormFieldDef[] =>
    config.fields.filter((f) => (f.step ?? 'advanced') === s && (!f.hidden || !f.hidden(form)));

  const step1Fields = useMemo(() => fieldsAtStep(1), [config.fields, form]);
  const step2Fields = useMemo(() => fieldsAtStep(2), [config.fields, form]);
  const step3Fields = useMemo(() => fieldsAtStep(3), [config.fields, form]);
  const advancedFields = useMemo(() => fieldsAtStep('advanced'), [config.fields, form]);

  const stepHasFields = step1Fields.length > 0 || step2Fields.length > 0 || step3Fields.length > 0;

  // Validation
  const stepIsValid = (s: 1 | 2 | 3): boolean => {
    const fs = fieldsAtStep(s);
    for (const f of fs) {
      if (!f.required) continue;
      const v = form[f.key];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return false;
    }
    return s === 1 ? targetValueValid : true;
  };

  const allRequiredSatisfied =
    targetValueValid && stepIsValid(1) && stepIsValid(2) && stepIsValid(3);

  const handleNext = () => {
    if (step === 1 && stepIsValid(1)) setStep(2);
    else if (step === 2 && stepIsValid(2)) setStep(3);
  };
  const handleBack = () => {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  };

  const handleAddAnother = () => {
    if (!onAddAnother || !allRequiredSatisfied) return;
    onAddAnother(form);
    setResetCounter((c) => c + 1);
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 bg-[#081549]/20 backdrop-blur-md flex items-center justify-center p-4 z-[9999] animate-fade-in" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_15px_50px_rgba(0,0,0,0.15)] overflow-hidden border border-slate-200/60 flex flex-col max-h-[85vh] animate-scale-up" onClick={(e) => e.stopPropagation()}>
        <header className="px-6 pt-5 pb-3 border-b border-slate-100 flex items-start justify-between relative bg-white select-none">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${TYPE_COLOR_SCHEME[type].iconBg} ${TYPE_COLOR_SCHEME[type].iconText} shrink-0 shadow-sm text-xs`}>
              {config.card.icon}
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                {existing ? 'Edit Target' : 'Add Target'}
              </span>
              <h3 className="text-sm font-extrabold text-slate-800 tracking-tight leading-snug">
                {config.card.label}
              </h3>
            </div>
          </div>
          <button
            type="button"
            className="bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-650 cursor-pointer p-1.5 rounded-xl transition duration-150 border-0 flex items-center justify-center"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={13} />
          </button>
        </header>

        {/* Thin elegant step progress bar */}
        <div className="w-full h-0.5 bg-slate-100 relative">
          <div
            className="h-full bg-[#58CC02] shadow-[0_0_8px_rgba(88,204,2,0.4)] transition-all duration-300 rounded-r"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        <div className="p-6 overflow-y-auto flex flex-col gap-4">
          {/* Step 1 — target value is always part of Basics */}
          {step === 1 && (
            <>
              <FieldShell label={config.targetLabel} required>
                <input
                  type="text"
                  className={`w-full h-10 px-3.5 bg-slate-50 border hover:border-slate-350 focus:bg-white focus:ring-4 focus:ring-[#081549]/5 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition duration-200 font-semibold ${
                    targetValueStr.length > 0 && !targetValueValid
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-500/5'
                      : 'border-slate-200 focus:border-[#081549]'
                  }`}
                  value={typeof form.target_value === 'string' ? (form.target_value as string) : ''}
                  onChange={(e) => setField('target_value', e.target.value)}
                  readOnly={readOnly}
                  placeholder={config.targetPlaceholder}
                  autoFocus
                  aria-label={config.targetLabel}
                />
                {targetValueStr.length > 0 && !targetValueValid && targetValidation.error && (
                  <span className="text-[10px] font-bold text-red-500 mt-1 select-none animate-fade-in block text-left">
                    ⚠️ {targetValidation.error}
                  </span>
                )}
              </FieldShell>

              {step1Fields.map((f) => (
                <FormFieldRenderer
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  readOnly={readOnly}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}
            </>
          )}

          {step === 2 && (
            <>
              {step2Fields.length === 0 && (
                <p className="text-xs font-semibold text-slate-400 italic text-center py-6">
                  No access questions for this category — continue.
                </p>
              )}
              {step2Fields.map((f) => (
                <FormFieldRenderer
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  readOnly={readOnly}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}
            </>
          )}

          {step === 3 && (
            <>
              {step3Fields.length === 0 && (
                <p className="text-xs font-semibold text-slate-400 italic text-center py-6">
                  No further details needed — save when ready.
                </p>
              )}
              {step3Fields.map((f) => (
                <FormFieldRenderer
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  readOnly={readOnly}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}

              {/* Collapsible Advanced section — only visible on final step */}
              {advancedFields.length > 0 && (
                <div className="mt-2.5 border-t border-dashed border-slate-200 pt-2.5">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 hover:bg-slate-150 rounded-lg text-slate-600 hover:text-slate-800 text-[10px] font-black uppercase tracking-wider transition duration-150 border-0 cursor-pointer"
                    onClick={() => setAdvancedOpen((v) => !v)}
                  >
                    {advancedOpen ? <ChevronDown size={12} className="text-slate-500" /> : <ChevronRight size={12} className="text-slate-500" />}
                    <span>Advanced testing rules</span>
                    <span className="text-[8px] font-extrabold text-slate-455 select-none lowercase italic">(optional)</span>
                  </button>
                  {advancedOpen && (
                    <div className="flex flex-col gap-3 mt-2.5 pl-2.5 border-l border-slate-200 animate-fade-in">
                      {advancedFields.map((f) => (
                        <FormFieldRenderer
                          key={f.key}
                          field={f}
                          value={form[f.key]}
                          readOnly={readOnly}
                          onChange={(v) => setField(f.key, v)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex items-center gap-2.5 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-3xl">
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 h-9 bg-white border border-slate-200 hover:border-slate-350 hover:bg-slate-50 rounded-xl text-slate-600 hover:text-slate-800 text-xs font-bold transition duration-150 shadow-sm cursor-pointer"
            onClick={onClose}
          >
            Cancel
          </button>
          {step > 1 && (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1 px-4 h-9 bg-white border border-[#081549] hover:bg-[#081549]/5 rounded-xl text-[#081549] text-xs font-black transition duration-150 shadow-sm cursor-pointer"
              onClick={handleBack}
            >
              <ArrowLeft size={11} /> Back
            </button>
          )}
          <span className="flex-1" />
          {step < 3 && (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1 px-5 h-9 bg-[#081549] hover:bg-[#000842] text-white rounded-xl text-xs font-black transition duration-150 shadow-md shadow-[#081549]/10 active:translate-y-px disabled:opacity-45 disabled:pointer-events-none cursor-pointer border-0"
              onClick={handleNext}
              disabled={!stepIsValid(step) || readOnly}
            >
              Continue <ArrowRight size={11} />
            </button>
          )}
          {step === 3 && (
            <>
              {onAddAnother && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-1 px-4 h-9 bg-white border border-[#58CC02] hover:bg-[#58CC02]/10 hover:border-[#58CC02] rounded-xl text-[#58CC02] text-xs font-black transition duration-150 shadow-sm disabled:opacity-45 cursor-pointer"
                  onClick={handleAddAnother}
                  disabled={!allRequiredSatisfied || readOnly}
                >
                  <Plus size={11} /> Save &amp; add another
                </button>
              )}
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1 px-5 h-9 bg-[#58CC02] hover:opacity-90 text-[#010D2C] rounded-xl text-xs font-black transition duration-150 shadow-md shadow-[#58CC02]/10 active:translate-y-px disabled:opacity-45 disabled:pointer-events-none cursor-pointer border-0"
                onClick={() => onSave(form)}
                disabled={!allRequiredSatisfied || readOnly}
              >
                <Check size={12} /> Save
              </button>
            </>
          )}
          {false && stepHasFields}
        </footer>
      </div>
    </div>,
    document.body
  );
}

function pillFromAccessLevels(levels: AccessLevel[]): string {
  if (levels.includes('NO_CREDENTIALS')) return 'no_credentials';
  if (levels.includes('ADMIN') && levels.includes('STANDARD_USER')) return 'multiple_roles';
  if (levels.includes('ADMIN')) return 'admin';
  if (levels.includes('STANDARD_USER')) return 'test_user';
  if (levels.includes('API_TOKEN')) return 'api_token';
  if (levels.includes('OAUTH_CLIENT')) return 'oauth_client';
  if (levels.includes('MTLS')) return 'mtls';
  if (levels.includes('READ_ONLY_CLOUD_ROLE')) return 'readonly_role';
  if (levels.includes('TBC')) return 'tbc';
  return '';
}

// ─── Field renderer ─────────────────────────────────────────────────────────

function FormFieldRenderer({
  field, value, readOnly, onChange,
}: {
  field: FormFieldDef;
  value: unknown;
  readOnly: boolean;
  onChange: (next: unknown) => void;
}) {
  if (field.type === 'text') {
    return (
      <FieldShell label={field.label} required={field.required} helpText={field.helpText}>
        <input
          type="text"
          className="w-full h-10 px-3.5 bg-slate-50 border border-slate-200 hover:border-slate-300 focus:bg-white focus:border-[#081549] focus:ring-4 focus:ring-[#081549]/5 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition duration-200 font-semibold"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          readOnly={readOnly}
          placeholder={field.placeholder}
          aria-label={field.label}
        />
      </FieldShell>
    );
  }
  if (field.type === 'long_text') {
    return (
      <FieldShell label={field.label} helpText={field.helpText}>
        <textarea
          className="w-full p-3 bg-slate-50 border border-slate-200 hover:border-slate-300 focus:bg-white focus:border-[#081549] focus:ring-4 focus:ring-[#081549]/5 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition duration-200 font-medium resize-none"
          rows={2}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
          readOnly={readOnly}
          placeholder={field.placeholder}
          aria-label={field.label}
        />
      </FieldShell>
    );
  }
  if (field.type === 'number') {
    return (
      <FieldShell label={field.label} helpText={field.helpText}>
        <input
          type="number"
          min={0}
          className="w-full h-10 px-3.5 bg-slate-50 border border-slate-200 hover:border-slate-300 focus:bg-white focus:border-[#081549] focus:ring-4 focus:ring-[#081549]/5 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition duration-200 font-semibold"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw.trim() === '') return onChange(undefined);
            const n = Number(raw);
            if (Number.isFinite(n) && n >= 0) onChange(Math.round(n));
          }}
          readOnly={readOnly}
          placeholder={field.placeholder}
          aria-label={field.label}
        />
      </FieldShell>
    );
  }
  if (field.type === 'yes_no') {
    return (
      <FieldShell label={field.label} required={field.required} helpText={field.helpText}>
        <div className="flex flex-wrap gap-2 pt-0.5 items-center">
          <button
            type="button"
            className={`px-4 py-1.5 border rounded-xl text-[10px] font-black uppercase tracking-wider transition duration-150 shadow-sm disabled:pointer-events-none cursor-pointer ${
              value === true
                ? 'bg-[#081549] border-[#081549] text-white shadow-sm shadow-[#081549]/10 hover:bg-[#000842] hover:border-[#000842] hover:text-white'
                : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-350 hover:bg-slate-100/50 hover:text-slate-700 font-semibold'
            }`}
            onClick={() => onChange(true)}
            disabled={readOnly}
          >
            Yes
          </button>
          <button
            type="button"
            className={`px-4 py-1.5 border rounded-xl text-[10px] font-black uppercase tracking-wider transition duration-150 shadow-sm disabled:pointer-events-none cursor-pointer ${
              value === false
                ? 'bg-[#081549] border-[#081549] text-white shadow-sm shadow-[#081549]/10 hover:bg-[#000842] hover:border-[#000842] hover:text-white'
                : 'bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-350 hover:bg-slate-100/50 hover:text-slate-700 font-semibold'
            }`}
            onClick={() => onChange(false)}
            disabled={readOnly}
          >
            No
          </button>
          {(value === true || value === false) && !readOnly && (
            <button
              type="button"
              className="inline-flex items-center gap-0.5 px-2.5 py-1 bg-white border border-slate-200 hover:border-slate-350 rounded-lg text-slate-550 hover:text-slate-700 text-[9px] font-extrabold transition duration-150 cursor-pointer shadow-sm"
              onClick={() => onChange(undefined)}
            >
              <X size={9} /> Clear
            </button>
          )}
        </div>
      </FieldShell>
    );
  }
  if (field.type === 'pill_choice' || field.type === 'single_choice') {
    return (
      <FieldShell label={field.label} required={field.required} helpText={field.helpText}>
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {field.options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`px-3.5 py-1.5 border rounded-xl text-[10px] font-black uppercase tracking-wider transition duration-150 shadow-sm disabled:pointer-events-none cursor-pointer ${
                value === o.value
                  ? 'bg-[#081549] border-[#081549] text-white shadow-sm shadow-[#081549]/10 hover:bg-[#000842] hover:border-[#000842] hover:text-white'
                  : 'bg-slate-50 border-slate-200 text-slate-550 hover:border-slate-350 hover:bg-slate-100/50 hover:text-slate-750 font-semibold'
              }`}
              onClick={() => onChange(o.value)}
              disabled={readOnly}
            >
              {o.label}
            </button>
          ))}
        </div>
      </FieldShell>
    );
  }
  if (field.type === 'multi_choice') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <FieldShell label={field.label} helpText={field.helpText}>
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {field.options.map((o) => {
            const checked = arr.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                className={`px-3.5 py-1.5 border rounded-xl text-[10px] font-black uppercase tracking-wider transition duration-150 shadow-sm disabled:pointer-events-none cursor-pointer ${
                  checked
                    ? 'bg-[#081549] border-[#081549] text-white shadow-sm shadow-[#081549]/10 hover:bg-[#000842] hover:border-[#000842] hover:text-white'
                    : 'bg-slate-50 border-slate-200 text-slate-550 hover:border-slate-350 hover:bg-slate-100/50 hover:text-slate-750 font-semibold'
                }`}
                onClick={() => onChange(checked ? arr.filter((v) => v !== o.value) : [...arr, o.value])}
                disabled={readOnly}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </FieldShell>
    );
  }
  return null;
}

function FieldShell({
  label, required, helpText, children,
}: {
  label: string;
  required?: boolean;
  helpText?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 select-none">
      <label className="text-[10px] font-black text-slate-500 tracking-wider uppercase flex items-center">
        <span>{label}</span>
        {required && <span className="text-red-500 font-black ml-0.5">*</span>}
      </label>
      {children}
      {helpText && <span className="text-[9px] font-bold text-slate-400 leading-normal">{helpText}</span>}
    </div>
  );
}
